import './PracticeTestCard.css';

import * as React from 'react';

import Button from '../../../../components/Button/Button';
import { Link } from 'react-router-dom';
import { PracticeTestScore } from '../../../../services/elearn/practice-test-types';
import PracticeTestScoreRow from './PracticeTestScoreRow/PracticeTestScoreRow';
import RadialProgress from '../../../../components/RadialProgress/RadialProgress';
import { getShortTestDisplayName, getTestDisplayName, isEnhancedActTest } from '../../../../services/utils/practice-test-util';
import { ALLOW_ENHANCED_ACT_DIGITAL_TESTS } from '../../../../rules/business'

export interface Props {
    score: PracticeTestScore;
    totalScore: number;
    showUnlockDialog?: () => void;
    diagnostic?: boolean;
    testType: string;
    showButton?: boolean
    showCircle?: boolean
    showTitle?: boolean
    onClick?: (link: string) => void
    freeTrialUser?: boolean
}

const PracticeTestCard: React.SFC<Props> = (props) => {
    const { testType, showButton=true, showCircle=true, showTitle=true, onClick, freeTrialUser=false } = props;
    const { testName, displayName, status, isActive, computerBasedTest, completedTime } = props.score;
    const testNumber = props.score.testName[props.score.testName.length - 1];

    let enhancedACT = isEnhancedActTest(testName)

    // In Enhanced ACT the science is optional and not used in average calculations (per Jennifer)!
    let sumScores = enhancedACT ? 
        props.score.english + props.score.math + props.score.reading
        :
        props.score.english + props.score.math + props.score.reading + props.score.science

    let denominator = enhancedACT ? 3: 4

    let radialScore = Math.round(sumScores / denominator);

    {/* tslint:disable:max-line-length */}
    let link = `/app/${(status === 'started' ||  status === 'completed') ? 
      `practice-tests/scoring/${testName}` : 
      `practice-tests/instructions/${testName}`}`;
    let skipLink = `practice-tests/scoring/${testName}`;
    if (props.diagnostic) {
      link = `/app/${(status === 'started' || status === 'completed') ? 
        `practice-tests/scoring/${testName}` : 
        `practice-tests/instructions/${testName}`}`;
    }
    if (testType === 'ia') {
      link = `/app/practice-tests/scoring/${testName}`;
    }

    const startedOrCompleted = (props.score.status === 'started' || props.score.status === 'completed');
    const button = createButton(startedOrCompleted, props.score.status);

    function handleButtonClick(testName) {
        if (onClick) {
            onClick(testName)
        }
    }

    function canComputerTestResume() {
        if (computerBasedTest) {
            let completed = completedTime && completedTime !== ""

            if (!completed) return true
        }

        return false
    }

    // New button covering the SAT (keeping the old as reference below)
    // as well as the new Enhanced ACT (both paper and computer-based digital tests)
    function createButton(startedOrCreated: boolean, status?) {
        let testDisplayName = displayName.toUpperCase()
        let {onClick} = props
        let isEnhancedAct = isEnhancedActTest(testName)
        let canUserTakeTest = !!isActive && (props.diagnostic || !freeTrialUser) // Free trial users can only take Diagnostic tests!

        if (testType === "sat") {
            let label = startedOrCompleted ? "REVIEW": "TAKE"

            if (canComputerTestResume()) {
                label = "RESUME"
            }

            return (
                <div>
                    {canUserTakeTest ?
                        <Button
                            className="practice-test-button"
                            text={`${startedOrCompleted ? 
                                `${label} ${displayName.toUpperCase()}` :
                                `${label} ${displayName.toUpperCase()}`}`
                                }
                            bgColor="blue"
                            onClick={(e) => handleButtonClick(testName)}
                        />
                        :
                         <Button
                            className="practice-test-button-locked"
                            text={`${startedOrCompleted ? 
                                `${label} ${displayName.toUpperCase()}` :
                                `${label} ${displayName.toUpperCase()}`}`
                                }
                            bgColor="gray"
                            onClick={props.showUnlockDialog}
                        />
                    }
                </div>
            )
        }

        if (isActive) {
            if (testType === 'ia') {
                return (
                    <Link to={link}>
                        <Button
                            className="practice-test-button"
                            text={`${startedOrCompleted ? 
                              `REVIEW ${displayName.toUpperCase()}` :
                              `ENTER ${displayName.toUpperCase()}`}`
                              }
                            bgColor="blue"
                        />
                    </Link>
                )
            }
            else {
                let buttonLabel = startedOrCompleted ? "REVIEW": "TAKE"

                if (canComputerTestResume()) {
                    buttonLabel = "RESUME"
                }

                if (isEnhancedAct) {
                    let shortTestDisplayName = getShortTestDisplayName(testName)
                    buttonLabel =  `${buttonLabel} ${shortTestDisplayName.toUpperCase()}`
                }
                else {
                    buttonLabel = `${buttonLabel} ${displayName.toUpperCase()}`
                }

                if (ALLOW_ENHANCED_ACT_DIGITAL_TESTS) {
                    return (
                        <div>
                            {canUserTakeTest ?
                                <Button
                                    className="practice-test-button"
                                    text={buttonLabel}
                                    bgColor="blue"
                                    onClick={(e) => handleButtonClick(testName)}
                                />
                                :
                                <Button
                                    className="practice-test-button-locked"
                                    text={buttonLabel}
                                    bgColor="gray"
                                    onClick={props.showUnlockDialog}
                                />
                            }
                        </div>
                    )
                }
                else {
                    return (
                        <div>
                            <Link to={link}>
                                <Button
                                    className="practice-test-button"
                                    text={buttonLabel}
                                    bgColor="blue"
                                />
                            </Link>
                            {!startedOrCompleted &&
                                <div className="pt-3">
                                    <Link to={skipLink} className="hyperlink" title="Skip Instructions and Enter Answers">
                                        Skip Instructions and Enter Answers
                                    </Link>
                                </div>
                            }
                        </div>
                    )
                }
            }
        }
        else {
            if( testName === 'Diagnostic' || testName === 'DiagnosticSAT') {
                return (
                    <Button
                        className="practice-test-button-locked"
                        text={`${startedOrCompleted ? 'REVIEW ' : 'TAKE '} ${displayName.toUpperCase()}`}
                        bgColor="gray"
                        onClick={props.showUnlockDialog}
                    />
                )
            } else if (testName.startsWith('ACT')) {
                return (
                    <Button
                        className="practice-test-button-locked"
                        text={`${startedOrCompleted ? 'REVIEW ACT TEST' : 'TAKE ACT TEST'} ${testNumber}`}
                        bgColor="gray"
                        onClick={props.showUnlockDialog}
                    />
                )
            } else if (testType === 'sat') {
                return (
                    <Button
                        className="practice-test-button-locked"
                        text={`${startedOrCompleted ? 'REVIEW SAT TEST' : 'TAKE SAT TEST'} ${testNumber}`}
                        bgColor="gray"
                        onClick={props.showUnlockDialog}
                    />
                )
            } else {
              // it is ia so we show no button
              return ""
            }
        }
    }

    // OT: Old code, not used (for reference only)
    function createButtonX(startedOrCreated: boolean) {
        if  (displayName.toLowerCase().includes('diagnostic')) {
            return (
                <div>
                    <Link to={link}>
                        <Button
                            className="practice-test-button"
                            text={`${startedOrCompleted ? 'REVIEW ' : 'TAKE '} ${displayName.toUpperCase()}`}
                            bgColor="blue"
                        />
                    </Link>
                    {!startedOrCompleted &&
                        <div className="pt-3">
                            <Link to={skipLink} className="hyperlink" title="Skip Instructions and Enter Answers">
                                Skip Instructions and Enter Answers
                            </Link>
                        </div>
                    }
                </div>
            );
        }
        if (isActive) {
            if( testName === 'Diagnostic' || testName === 'DiagnosticSAT') {
                return (
                    <div>
                        <Link to={link}>
                        <Button
                            className="practice-test-button"
                            text={`${startedOrCompleted ? 'REVIEW ' : 'TAKE '} ${displayName.toUpperCase()}`}
                            bgColor="blue"
                        />
                    </Link>
                    {!startedOrCompleted && 
                        <div className="pt-3">
                            <Link to={skipLink} className="hyperlink" title="Skip Instructions and Enter Answers">
                                Skip Instructions and Enter Answers
                            </Link>
                        </div>
                    }
                    </div>
                );
            } else if (testName.startsWith('ACT')) {
                return (
                    <div>
                        <Link to={link}>
                            <Button
                                className="practice-test-button"
                                text={`${startedOrCompleted ? 'REVIEW ACT TEST' : 'TAKE ACT TEST'} ${testNumber}`}
                                bgColor="blue"
                            />
                        </Link>
                        {!startedOrCompleted && 
                            <div className="pt-3">
                                <Link to={skipLink} className="hyperlink" title="Skip Instructions and Enter Answers">
                                    Skip Instructions and Enter Answers
                                </Link>
                            </div>
                        }
                    </div>
                );
            } else if (testType === 'ia') {
                return (
                    <Link to={link}>
                        <Button
                            className="practice-test-button"
                            text={`${startedOrCompleted ? 
                              `REVIEW ${displayName.toUpperCase()} ANSWERS` :
                              `ENTER ${displayName.toUpperCase()} ANSWERS`}`
                              }
                            bgColor="blue"
                        />
                    </Link>
                );
            } else {
                return (
                    <div>
                        <Link to={link}>
                            <Button
                                className="practice-test-button"
                                text={`${startedOrCompleted ? 'REVIEW SAT TEST' : 'TAKE SAT TEST'} ${testNumber}`}
                                bgColor="blue"
                            />
                        </Link>
                        {!startedOrCompleted && 
                            <div className="pt-3">
                                <Link to={skipLink} className="hyperlink" title="Skip Instructions and Enter Answers">
                                    Skip Instructions and Enter Answers
                                </Link>
                            </div>
                        }
                    </div>
                );
            }

        } else {
            if( testName === 'Diagnostic' || testName === 'DiagnosticSAT') {
                return (
                    <Button
                        className="practice-test-button-locked"
                        text={`${startedOrCompleted ? 'REVIEW ' : 'TAKE '} ${displayName.toUpperCase()}`}
                        bgColor="gray"
                        onClick={props.showUnlockDialog}
                    />
                );
            } else if (testName.startsWith('ACT')) {
                return (
                    <Button
                        className="practice-test-button-locked"
                        text={`${startedOrCompleted ? 'REVIEW ACT TEST' : 'TAKE ACT TEST'} ${testNumber}`}
                        bgColor="gray"
                        onClick={props.showUnlockDialog}
                    />
                );
            } else if (testType === 'sat') {
                return (
                    <Button
                        className="practice-test-button-locked"
                        text={`${startedOrCompleted ? 'REVIEW SAT TEST' : 'TAKE SAT TEST'} ${testNumber}`}
                        bgColor="gray"
                        onClick={props.showUnlockDialog}
                    />
                );
            } else {
              // it is ia so we show no button;
              return '';
            }
        }
    }

    if (testType === 'enhancedACT' || testType === 'act' || testType === 'ia' ) {
        let newScore = radialScore + '';
        let margin = 0;
        return (
            <div className="practice-test-card-container">
                <div className="practice-test-card-subcontainer">
                    <div className="practice-test-title">
                        {showTitle &&
                            <h3 className="practice-test-title-text">{displayName}</h3>
                        }

                        <div className={`${!startedOrCompleted ? 'faded' : ''}`}>
                            {showCircle && 
                                <RadialProgress
                                    size={150}
                                    radius={45}
                                    startColor="#05b4b9"
                                    endColor="#05b4b9"
                                    pegOffset={30}
                                    progress={(!startedOrCompleted ? 0 : radialScore / props.totalScore * 100)}
                                    text={(!startedOrCompleted ? '0' : radialScore + '')}
                                    textStyle={{fontSize: '33px', marginLeft: margin, color: '#2B2D2D'}}
                                    progressLineWidth={10}
                                    backgroundLineWidth={10}
                                />
                            }
                        </div>
                    </div>
                    {!isActive && testType === 'ia' &&
                      <p className="ia-unassigned-message">
                        This test has not yet been activated by your school administrator
                      </p>
                    }

                    {/* tslint:disable:max-line-length */}
                    <div className={'practice-test-scores-container ' + (!startedOrCompleted ? 'faded' : '')}>
                        <PracticeTestScoreRow subject="English" score={(!startedOrCompleted ? 0 : props.score.english)} totalScore={props.totalScore} barColor="purple" />
                        <PracticeTestScoreRow subject="Math" score={(!startedOrCompleted ? 0 : props.score.math)} totalScore={props.totalScore} barColor="blue" />
                        <PracticeTestScoreRow subject="Reading" score={(!startedOrCompleted ? 0 : props.score.reading)} totalScore={props.totalScore} barColor="orange" />
                        {enhancedACT && <hr/>}
                        <PracticeTestScoreRow subject="Science" score={(!startedOrCompleted ? 0 : props.score.science)} totalScore={props.totalScore} barColor="green" />
                    </div>
                     {/* Button */}
                    { showButton &&
                        <div className="practice-test-button-container">
                           {button}
                        </div>
                    }
                </div>
            </div>
        );
    } else {
        let totalScore = 1600;
        sumScores = props.score.math + props.score.evidenceBasedRW;
        radialScore = sumScores;
        let newScore = radialScore + '';
        let margin = 0
        return (
            <div className="practice-test-card-container">
                <div className="practice-test-card-subcontainer">
                    {/* Title */}
                    <div className="practice-test-title">
                        {showTitle &&
                            <h3 className="practice-test-title-text">{displayName}</h3>
                        }

                        <div className={(!startedOrCompleted ? 'faded' : '')}>
                            {showCircle &&
                                <RadialProgress
                                    size={150}
                                    radius={45}
                                    startColor="#05b4b9"
                                    endColor="#05b4b9"
                                    pegOffset={30}
                                    progress={(!startedOrCompleted ? 0 : radialScore / totalScore * 100)}
                                    text={(!startedOrCompleted ? '0' : radialScore + '')}
                                    textStyle={{fontSize: '33px', marginLeft: margin, color: '#2B2D2D'}}
                                    progressLineWidth={10}
                                    backgroundLineWidth={10}
                                />
                            }
                        </div>
                    </div>

                    {/* tslint:disable:max-line-length */}
                    <div className={'practice-test-scores-container ' + (!startedOrCompleted ? 'faded' : '')}>
                        <PracticeTestScoreRow subject="EBRW" score={(!startedOrCompleted ? 0 : props.score.evidenceBasedRW)} totalScore={800} barColor="purple" />
                        <PracticeTestScoreRow subject="Math" score={(!startedOrCompleted ? 0 : props.score.math)} totalScore={800} barColor="blue" />
                        <PracticeTestScoreRow subject="EBRW" score={(!startedOrCompleted ? 0 : props.score.evidenceBasedRW)} totalScore={800} barColor="purple" className="invisible" />
                        <PracticeTestScoreRow subject="Math" score={(!startedOrCompleted ? 0 : props.score.math)} totalScore={800} barColor="blue" className="invisible" />
                    </div>

                    {/* Button */}
                    { showButton &&
                        <div className="practice-test-button-container">
                            {button}
                        </div>
                    }
                </div>
            </div>
        )
    }
}

export default PracticeTestCard;
